import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Download, FileXCorner, Upload } from "lucide-react";

import Filters from "../../../Filter";
import { UIButton, UIEmptyState, UIPaginationBar } from "../../../ui";
import ClientReportPreviewModal from "./ClientReportPreviewModal";
import ClientReportsTable from "./ClientReportsTable";
import DeleteClientReportModal from "./DeleteClientReportModal";
import DownloadAllClientReportsModal from "./DownloadAllClientReportsModal";
import DownloadClientReportModal from "./DownloadClientReportModal";
import UploadClientReportModal from "./UploadClientReportModal";
import messages from "../../../../assets/locale/messages";
import { showErrorMsg, showSuccessMsg } from "../../../../utils/Helpers";
import {
  buildClientReportsAllParams,
  buildClientReportsListParams,
  mapClientReportRow,
} from "./clientReportMappers";
import {
  buildClientReportPayload,
  downloadClientReportFile,
} from "./clientReportUpload";
import { CLIENT_REPORTS_DEFAULT_PAGE_SIZE } from "./constants";

import "../Reports/ReportsShared.scss";
import "./ClientReports.scss";

const INITIAL_META = { count: 0, pages: 1 };

const readListMeta = (response) => ({
  count: Number(response?.headers?.["total-count"] || 0),
  pages: Number(response?.headers?.["total-pages"] || 1),
});

const ClientReportsTab = ({
  event,
  api,
  canUpload,
  canDelete,
  canDownloadAll,
  canPreview,
  downloadErrorMessage,
  onForbidden,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const { clientReports, shared } = messages[lang];
  const downloadError = downloadErrorMessage || clientReports.downloadError;

  const eventId = event?.id;

  const [list, setList] = useState([]);
  const [meta, setMeta] = useState(INITIAL_META);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(CLIENT_REPORTS_DEFAULT_PAGE_SIZE);
  const [isLoading, setIsLoading] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [reportToDownload, setReportToDownload] = useState(null);
  const [reportToPreview, setReportToPreview] = useState(null);
  const [reportToDelete, setReportToDelete] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [showDownloadAllModal, setShowDownloadAllModal] = useState(false);
  const [downloadAllList, setDownloadAllList] = useState([]);
  const [downloadAllLoading, setDownloadAllLoading] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);

  useEffect(() => {
    if (!eventId) return undefined;

    let cancelled = false;
    setIsLoading(true);

    (async () => {
      try {
        const response = await api.getList(
          eventId,
          buildClientReportsListParams({ page, pageSize }),
        );
        if (cancelled) return;
        setList(response?.data?.data ?? []);
        setMeta(readListMeta(response));
      } catch (error) {
        if (cancelled) return;
        setList([]);
        setMeta(INITIAL_META);
        // 403 means the event is out of this account's reach — leave, don't retry.
        if (error?.response?.status === 403) onForbidden?.();
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // `lang` refetches on purpose — `creator.type` comes back localized.
  }, [api, eventId, page, pageSize, reloadToken, onForbidden, lang]);

  const rows = useMemo(() => (list || []).map(mapClientReportRow), [list]);

  const totalCount = meta.count || 0;
  const paginationPages =
    meta.pages || Math.max(Math.ceil(totalCount / pageSize), 1);
  const paginationFrom = totalCount > 0 ? (page - 1) * pageSize + 1 : 0;
  const paginationTo = Math.min(page * pageSize, totalCount);

  const handleUpload = useCallback(
    async (file, uploadUrl) => {
      if (!eventId) return;

      setSubmitting(true);
      try {
        await api.create(eventId, buildClientReportPayload(file, uploadUrl));
        setShowUploadModal(false);
        setPage(1);
        setReloadToken((token) => token + 1);
      } catch (error) {
        // the axios interceptor surfaces the error message
      } finally {
        setSubmitting(false);
      }
    },
    [api, eventId],
  );

  // Re-read the report so the link is fresh, falling back to the listed one.
  const resolveDownloadUrl = useCallback(
    async (report) => {
      try {
        const response = await api.getFile(eventId, report.id);
        return response?.data?.data?.upload_url || report.uploadUrl;
      } catch (error) {
        return report.uploadUrl;
      }
    },
    [api, eventId],
  );

  const handleDownload = useCallback(
    async (report) => {
      if (!eventId || downloading) return;

      setDownloading(true);
      try {
        const url = await resolveDownloadUrl(report);
        await downloadClientReportFile(url, report.name);
        setReportToDownload(null);
      } catch (error) {
        showErrorMsg(downloadError);
      } finally {
        setDownloading(false);
      }
    },
    [downloadError, downloading, eventId, resolveDownloadUrl],
  );

  const handleDelete = useCallback(
    async (report) => {
      if (!eventId || !report?.id || deleting) return;

      setDeleting(true);
      try {
        await api.remove(eventId, report.id);
        setReportToDelete(null);
        showSuccessMsg(clientReports.deleteModal.success, true);
        // Drop back a page when the last row on a non-first page is removed,
        // otherwise reload the current page in place.
        if (list.length === 1 && page > 1) {
          setPage((current) => current - 1);
        } else {
          setReloadToken((token) => token + 1);
        }
      } catch (error) {
        setReportToDelete(null);
        showErrorMsg(clientReports.deleteModal.error);
        // Re-sync the list: a 404 (already deleted elsewhere) drops the stale
        // row; a transient failure just re-reads the unchanged list.
        setReloadToken((token) => token + 1);
      } finally {
        setDeleting(false);
      }
    },
    [api, clientReports.deleteModal, deleting, eventId, list.length, page],
  );

  const handleOpenDownloadAll = useCallback(async () => {
    if (!eventId) return;

    setShowDownloadAllModal(true);
    setDownloadAllLoading(true);
    try {
      const response = await api.getList(eventId, buildClientReportsAllParams());
      setDownloadAllList((response?.data?.data ?? []).map(mapClientReportRow));
    } catch (error) {
      setDownloadAllList([]);
    } finally {
      setDownloadAllLoading(false);
    }
  }, [api, eventId]);

  const handleDownloadAll = useCallback(async () => {
    if (!downloadAllList.length || downloadingAll) return;

    setDownloadingAll(true);
    try {
      for (const report of downloadAllList) {
        // sequential so the browser does not drop concurrent save prompts
        // eslint-disable-next-line no-await-in-loop
        const url = await resolveDownloadUrl(report);
        // eslint-disable-next-line no-await-in-loop
        await downloadClientReportFile(url, report.name);
      }
      setShowDownloadAllModal(false);
    } catch (error) {
      showErrorMsg(downloadError);
    } finally {
      setDownloadingAll(false);
    }
  }, [downloadError, downloadAllList, downloadingAll, resolveDownloadUrl]);

  const uploadModalLabels = useMemo(
    () => ({
      title: clientReports.uploadModal.title,
      description: clientReports.uploadModal.description,
      cancel: shared.cancel,
      submit: clientReports.uploadModal.submit,
      uploadClickToUpload: clientReports.uploadModal.uploadClickToUpload,
      uploadDragHint: clientReports.uploadModal.uploadDragHint,
      uploadHint: clientReports.uploadModal.uploadHint,
      uploadTypeError: clientReports.uploadModal.uploadTypeError,
      uploadMaxSizeError: clientReports.uploadModal.uploadMaxSizeError,
      readyToUpload: clientReports.uploadModal.readyToUpload,
      removeFile: clientReports.uploadModal.removeFile,
    }),
    [clientReports.uploadModal, shared.cancel],
  );

  const downloadModalLabels = useMemo(
    () => ({
      title: clientReports.downloadModal.title,
      cancel: shared.cancel,
      download: shared.download,
      readyToDownload: clientReports.downloadModal.readyToDownload,
    }),
    [clientReports.downloadModal, shared.cancel, shared.download],
  );

  const deleteModalLabels = useMemo(
    () => ({
      title: clientReports.deleteModal.title,
      uploadedOn: clientReports.deleteModal.uploadedOn,
      warning: clientReports.deleteModal.warning,
      confirmQuestion: clientReports.deleteModal.confirmQuestion,
      cancel: shared.cancel,
      confirm: clientReports.deleteModal.confirm,
    }),
    [clientReports.deleteModal, shared.cancel],
  );

  const downloadAllModalLabels = useMemo(
    () => ({
      title: clientReports.downloadAllModal.title,
      cancel: shared.cancel,
      submit: clientReports.downloadAllModal.submit,
      selectedCount: clientReports.downloadAllModal.selectedCount,
      readyToDownload: clientReports.downloadModal.readyToDownload,
      loading: clientReports.downloadAllModal.loading,
    }),
    [
      clientReports.downloadAllModal,
      clientReports.downloadModal.readyToDownload,
      shared.cancel,
    ],
  );

  const previewModalLabels = useMemo(
    () => ({
      download: shared.download,
      close: shared.close,
      unsupported: clientReports.previewModal.unsupportedType,
      loadError: clientReports.previewModal.loadError,
      pageOf: clientReports.previewModal.pageOf,
      previousPage: clientReports.previewModal.previousPage,
      nextPage: clientReports.previewModal.nextPage,
      sheetTruncated: clientReports.previewModal.sheetTruncated,
      unknownUploader: clientReports.unknownUploader,
    }),
    [
      clientReports.previewModal,
      clientReports.unknownUploader,
      shared.close,
      shared.download,
    ],
  );

  const tableLabels = useMemo(
    () => ({
      fileName: clientReports.fileName,
      size: clientReports.size,
      uploadedBy: clientReports.uploadedBy,
      uploadDate: clientReports.uploadDate,
      actions: clientReports.actions,
      download: shared.download,
      delete: clientReports.delete,
      fileTypes: clientReports.fileTypes,
      unknownUploader: clientReports.unknownUploader,
    }),
    [clientReports, shared.download],
  );

  if (!eventId) return null;

  const showHeaderActions = canDownloadAll || canUpload;

  return (
    <div className="client-reports">
      {showHeaderActions ? (
        <Filters
          isShowSearch={false}
          isCollapsable
          hideFilterToggle
          filters={[]}
          renderActionBeforeFilterToggle={
            <>
              {canDownloadAll ? (
                <UIButton
                  type="button"
                  variant="primary"
                  appearance="outline"
                  size="large"
                  startIcon={<Download size={16} aria-hidden />}
                  disabled={!totalCount}
                  onClick={handleOpenDownloadAll}
                >
                  {clientReports.downloadAll}
                </UIButton>
              ) : null}
              {canUpload ? (
                <UIButton
                  type="button"
                  variant="primary"
                  appearance="outline"
                  size="large"
                  startIcon={<Upload size={16} aria-hidden />}
                  onClick={() => setShowUploadModal(true)}
                >
                  {clientReports.uploadReport}
                </UIButton>
              ) : null}
            </>
          }
          classNames={{ container: "reports-list-filters" }}
        />
      ) : null}

      {isLoading ? null : !rows.length ? (
        <UIEmptyState
          classNames={{ root: "reports-empty" }}
          title={clientReports.emptyTitle}
          description={clientReports.emptyDescription}
          icon={<FileXCorner size={40} strokeWidth={2.5} aria-hidden />}
          actionLabel={canUpload ? clientReports.uploadReport : undefined}
          actionIcon={canUpload ? <Upload aria-hidden /> : undefined}
          onAction={canUpload ? () => setShowUploadModal(true) : undefined}
        />
      ) : (
        <>
          <ClientReportsTable
            rows={rows}
            labels={tableLabels}
            pageSize={pageSize}
            canDelete={canDelete}
            canPreview={canPreview}
            onDownload={setReportToDownload}
            onDelete={setReportToDelete}
            onPreview={setReportToPreview}
          />
          {totalCount > 0 ? (
            <UIPaginationBar
              currentPage={page}
              pages={paginationPages}
              pageSize={pageSize}
              rangeTemplate={clientReports.showingRange}
              rangeFrom={paginationFrom}
              rangeTo={paginationTo}
              rangeTotal={totalCount}
              rowsPerPageLabel={shared.numberOfRows}
              onPageChange={setPage}
              onPageSizeChange={(nextSize) => {
                setPageSize(nextSize);
                setPage(1);
              }}
              className="reports-list-pagination"
            />
          ) : null}
        </>
      )}

      <UploadClientReportModal
        open={showUploadModal}
        labels={uploadModalLabels}
        submitting={submitting}
        onClose={() => setShowUploadModal(false)}
        onSubmit={handleUpload}
      />

      <DownloadClientReportModal
        open={Boolean(reportToDownload)}
        labels={downloadModalLabels}
        report={reportToDownload}
        downloading={downloading}
        onClose={() => setReportToDownload(null)}
        onDownload={handleDownload}
      />

      <DeleteClientReportModal
        open={Boolean(reportToDelete)}
        labels={deleteModalLabels}
        report={reportToDelete}
        deleting={deleting}
        onClose={() => setReportToDelete(null)}
        onConfirm={handleDelete}
      />

      {canPreview ? (
        <ClientReportPreviewModal
          open={Boolean(reportToPreview)}
          report={reportToPreview}
          labels={previewModalLabels}
          isRtl={lang === "ar"}
          downloading={downloading}
          resolveUrl={resolveDownloadUrl}
          onDownload={handleDownload}
          onClose={() => setReportToPreview(null)}
        />
      ) : null}

      {canDownloadAll ? (
        <DownloadAllClientReportsModal
          open={showDownloadAllModal}
          labels={downloadAllModalLabels}
          reports={downloadAllList}
          loading={downloadAllLoading}
          downloading={downloadingAll}
          onClose={() => setShowDownloadAllModal(false)}
          onDownload={handleDownloadAll}
        />
      ) : null}
    </div>
  );
};

ClientReportsTab.propTypes = {
  event: PropTypes.object,
  /** Portal-specific endpoints — see companyPortalClientReports / clientPortalClientReports. */
  api: PropTypes.shape({
    getList: PropTypes.func.isRequired,
    getFile: PropTypes.func.isRequired,
    /** Required unless `canUpload` is false (read-only Admin view). */
    create: PropTypes.func,
    /** Required unless `canDelete` is false (read-only Admin view). */
    remove: PropTypes.func,
  }).isRequired,
  canUpload: PropTypes.bool,
  /** Gates the Delete control alongside each report's own `is_deletable`. */
  canDelete: PropTypes.bool,
  /** Shows the "Download All" action. Off for the read-only Admin view. */
  canDownloadAll: PropTypes.bool,
  /** Row click opens the file preview. Off for the read-only Admin view. */
  canPreview: PropTypes.bool,
  /** Overrides the download-failure toast copy (Admin AC wording differs). */
  downloadErrorMessage: PropTypes.string,
  /** Called when the list answers 403 — the event is out of this account's reach. */
  onForbidden: PropTypes.func,
};

ClientReportsTab.defaultProps = {
  event: null,
  canUpload: true,
  canDelete: true,
  canDownloadAll: true,
  canPreview: true,
  downloadErrorMessage: undefined,
  onForbidden: undefined,
};

export default ClientReportsTab;
